import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, ChevronLeft, Check, Car, User, Target, Send, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, label: 'About You', icon: User },
  { id: 2, label: 'Your Vehicles', icon: Car },
  { id: 3, label: 'Your Lifestyle', icon: Target },
  { id: 4, label: 'Your Goals', icon: Send },
];

const LifestyleAssessment = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    city: '', zipCode: '',
    currentVehicles: '', vehicleCount: '',
    annualMileage: '', primaryUse: '',
    ownOrLease: '', buyingTimeline: '',
    transportationNeeds: [], eventFrequency: '',
    monthlyBudget: '', membershipInterest: '',
    priorities: [], preferredContact: 'phone',
    additionalNotes: '',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleArray = (field, value) => {
    setForm(prev => {
      const arr = prev[field];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/lifestyle-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      toast({ title: 'Assessment Submitted!', description: 'Your ACE concierge will reach out within 24 hours.' });
    } catch {
      toast({ title: 'Error', description: 'Could not submit. Please try again or call us directly.', variant: 'destructive' });
    }
    setSubmitting(false);
  };

  const canNext = () => {
    if (step === 1) return form.firstName && form.lastName && form.email && form.phone;
    if (step === 2) return form.vehicleCount;
    if (step === 3) return form.eventFrequency;
    return true;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="assessment-success">
        <div className="bg-black text-white py-20 sm:py-28">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">Assessment Complete</h1>
            <p className="text-base text-gray-300 mb-8">
              Thank you for completing the Auto Lifestyle Assessment. Your dedicated ACE concierge will review your profile and reach out within 24 hours.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button onClick={() => navigate('/membership')} className="bg-red-600 hover:bg-red-700 text-white h-12 px-6">
                View Memberships
              </Button>
              <Button onClick={() => navigate('/')} variant="outline" className="border-white text-white hover:bg-white hover:text-black h-12 px-6">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24 sm:pb-0" data-testid="lifestyle-assessment-page">
      {/* Hero */}
      <div className="bg-black text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs sm:text-sm text-red-500 font-semibold tracking-widest mb-3">AUTO LIFESTYLE ASSESSMENT</p>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3">
            Discover Your <em className="text-red-500 not-italic">ACE Tier</em>
          </h1>
          <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto">
            Answer a few questions about your auto lifestyle and we'll match you with the right concierge experience.
          </p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.id} className={`flex items-center gap-1.5 text-xs font-semibold ${step === s.id ? 'text-red-600' : step > s.id ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold
                    ${step === s.id ? 'bg-red-600 text-white' : step > s.id ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                    {step > s.id ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
        <Card className="p-6 sm:p-8 border-gray-200">
          {/* Step 1: About You */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-1-about">
              <div>
                <h2 className="text-xl font-bold text-black mb-1">About You</h2>
                <p className="text-sm text-gray-500">Let us know who you are so we can personalize your concierge experience.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-black mb-1.5 block text-sm">First Name *</Label>
                  <Input value={form.firstName} onChange={e => update('firstName', e.target.value)} className="h-11 border-gray-200" data-testid="assess-first-name" />
                </div>
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Last Name *</Label>
                  <Input value={form.lastName} onChange={e => update('lastName', e.target.value)} className="h-11 border-gray-200" data-testid="assess-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="h-11 border-gray-200" data-testid="assess-email" />
                </div>
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Phone *</Label>
                  <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(404) 555-1234" className="h-11 border-gray-200" data-testid="assess-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-black mb-1.5 block text-sm">City</Label>
                  <Input value={form.city} onChange={e => update('city', e.target.value)} placeholder="Atlanta" className="h-11 border-gray-200" data-testid="assess-city" />
                </div>
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Zip Code</Label>
                  <Input value={form.zipCode} onChange={e => update('zipCode', e.target.value)} placeholder="30301" className="h-11 border-gray-200" data-testid="assess-zip" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Your Vehicles */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-2-vehicles">
              <div>
                <h2 className="text-xl font-bold text-black mb-1">Your Vehicles</h2>
                <p className="text-sm text-gray-500">Tell us about your current vehicle situation.</p>
              </div>
              <div>
                <Label className="text-black mb-1.5 block text-sm">How many vehicles do you currently own/lease? *</Label>
                <div className="flex gap-2 flex-wrap">
                  {['0', '1', '2', '3', '4', '5+'].map(v => (
                    <button key={v} type="button" onClick={() => update('vehicleCount', v)}
                      className={`px-5 py-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${
                        form.vehicleCount === v ? 'border-red-600 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                      }`} data-testid={`vehicle-count-${v}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-black mb-1.5 block text-sm">Current vehicle(s) — make, model, year</Label>
                <Textarea value={form.currentVehicles} onChange={e => update('currentVehicles', e.target.value)}
                  placeholder="e.g. 2023 BMW X5, 2022 Mercedes S-Class..." rows={2} className="border-gray-200" data-testid="assess-current-vehicles" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Estimated annual mileage</Label>
                  <Select value={form.annualMileage} onValueChange={v => update('annualMileage', v)}>
                    <SelectTrigger className="h-11 border-gray-200" data-testid="assess-mileage"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {['Under 5,000', '5,000 - 10,000', '10,000 - 20,000', '20,000 - 30,000', '30,000+'].map(v => (
                        <SelectItem key={v} value={v}>{v} miles</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Primary vehicle use</Label>
                  <Select value={form.primaryUse} onValueChange={v => update('primaryUse', v)}>
                    <SelectTrigger className="h-11 border-gray-200" data-testid="assess-primary-use"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {['Daily commute', 'Business/client meetings', 'Weekend/leisure', 'Family', 'Content creation', 'Collection/investment'].map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Own or Lease?</Label>
                  <div className="flex gap-2">
                    {['Own', 'Lease', 'Both', 'Neither'].map(v => (
                      <button key={v} type="button" onClick={() => update('ownOrLease', v)}
                        className={`flex-1 py-2.5 rounded-lg border-2 text-xs font-semibold transition-all ${
                          form.ownOrLease === v ? 'border-red-600 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600'
                        }`} data-testid={`own-lease-${v.toLowerCase()}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-black mb-1.5 block text-sm">Planning to buy/sell within</Label>
                  <Select value={form.buyingTimeline} onValueChange={v => update('buyingTimeline', v)}>
                    <SelectTrigger className="h-11 border-gray-200" data-testid="assess-timeline"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {['Not currently', '1-3 months', '3-6 months', '6-12 months', '12+ months'].map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Your Lifestyle */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-3-lifestyle">
              <div>
                <h2 className="text-xl font-bold text-black mb-1">Your Lifestyle</h2>
                <p className="text-sm text-gray-500">Help us understand how you use transportation and events.</p>
              </div>
              <div>
                <Label className="text-black mb-2 block text-sm">What transportation services interest you? (Select all)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'Airport transfers', 'Corporate/business travel',
                    'Wedding & event transport', 'Night out / special occasions',
                    'Exotic car experiences', 'Group/Sprinter charters',
                    'Content creation / shoots', 'Vehicle sourcing & consulting',
                  ].map(v => (
                    <button key={v} type="button" onClick={() => toggleArray('transportationNeeds', v)}
                      className={`text-left px-3 py-2.5 rounded-lg border-2 text-xs font-medium transition-all ${
                        form.transportationNeeds.includes(v) ? 'border-red-600 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                      }`} data-testid={`need-${v.replace(/\s/g, '-').toLowerCase()}`}>
                      {form.transportationNeeds.includes(v) && <Check className="inline h-3 w-3 mr-1" />}
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-black mb-1.5 block text-sm">How often do you attend events requiring chauffeured transport? *</Label>
                <div className="flex gap-2 flex-wrap">
                  {['Rarely', 'Monthly', '2-3x/month', 'Weekly', 'Multiple/week'].map(v => (
                    <button key={v} type="button" onClick={() => update('eventFrequency', v)}
                      className={`px-4 py-2.5 rounded-lg border-2 text-xs font-semibold transition-all ${
                        form.eventFrequency === v ? 'border-red-600 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600'
                      }`} data-testid={`freq-${v.replace(/\//g, '-').toLowerCase()}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-black mb-1.5 block text-sm">Monthly transportation budget</Label>
                <Select value={form.monthlyBudget} onValueChange={v => update('monthlyBudget', v)}>
                  <SelectTrigger className="h-11 border-gray-200" data-testid="assess-budget"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {['Under $200', '$200 - $500', '$500 - $1,000', '$1,000 - $2,500', '$2,500 - $5,000', '$5,000+'].map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 4: Your Goals */}
          {step === 4 && (
            <div className="space-y-5" data-testid="step-4-goals">
              <div>
                <h2 className="text-xl font-bold text-black mb-1">Your Goals</h2>
                <p className="text-sm text-gray-500">What matters most to you in a concierge experience?</p>
              </div>
              <div>
                <Label className="text-black mb-2 block text-sm">Top priorities (Select up to 3)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'Save time on logistics', 'Access luxury/exotic vehicles',
                    'Impress clients & guests', 'Reliable corporate transport',
                    'Vehicle buying/selling help', 'Content & brand building',
                    'Reduce cost vs. on-demand', 'VIP event experiences',
                  ].map(v => (
                    <button key={v} type="button"
                      onClick={() => {
                        if (form.priorities.includes(v)) {
                          toggleArray('priorities', v);
                        } else if (form.priorities.length < 3) {
                          toggleArray('priorities', v);
                        }
                      }}
                      className={`text-left px-3 py-2.5 rounded-lg border-2 text-xs font-medium transition-all ${
                        form.priorities.includes(v) ? 'border-red-600 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                      }`} data-testid={`priority-${v.replace(/\s/g, '-').toLowerCase()}`}>
                      {form.priorities.includes(v) && <Check className="inline h-3 w-3 mr-1" />}
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-black mb-1.5 block text-sm">Which membership tier interests you?</Label>
                <Select value={form.membershipInterest} onValueChange={v => update('membershipInterest', v)}>
                  <SelectTrigger className="h-11 border-gray-200" data-testid="assess-membership-interest"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lite-starter">Lite Starter — $25/mo</SelectItem>
                    <SelectItem value="lite-plus">Lite Plus — $95/mo</SelectItem>
                    <SelectItem value="lite-premier">Lite Premier — $195/mo</SelectItem>
                    <SelectItem value="full-essentials">Full-Access Essentials — $297/mo</SelectItem>
                    <SelectItem value="full-executive">Full-Access Executive — $697/mo</SelectItem>
                    <SelectItem value="full-black">Full-Access Black Card — $1,497/mo</SelectItem>
                    <SelectItem value="not-sure">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-black mb-1.5 block text-sm">Preferred contact method</Label>
                <div className="flex gap-2">
                  {[{ v: 'phone', l: 'Phone Call' }, { v: 'text', l: 'Text' }, { v: 'email', l: 'Email' }].map(o => (
                    <button key={o.v} type="button" onClick={() => update('preferredContact', o.v)}
                      className={`flex-1 py-2.5 rounded-lg border-2 text-xs font-semibold transition-all ${
                        form.preferredContact === o.v ? 'border-red-600 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600'
                      }`} data-testid={`contact-pref-${o.v}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-black mb-1.5 block text-sm">Anything else you'd like us to know?</Label>
                <Textarea value={form.additionalNotes} onChange={e => update('additionalNotes', e.target.value)}
                  placeholder="Special interests, upcoming events, questions..." rows={3} className="border-gray-200" data-testid="assess-notes" />
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            {step > 1 ? (
              <Button type="button" onClick={() => setStep(step - 1)} variant="outline" className="h-12 px-6" data-testid="assess-back-btn">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            ) : <div />}
            
            {step < 4 ? (
              <Button type="button" onClick={() => setStep(step + 1)} disabled={!canNext()}
                className="h-12 px-8 bg-black hover:bg-gray-800 text-white" data-testid="assess-next-btn">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={submitting}
                className="h-12 px-8 bg-red-600 hover:bg-red-700 text-white" data-testid="assess-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {submitting ? 'Submitting...' : 'Submit Assessment'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LifestyleAssessment;
